import { useState } from 'react';
import { LogOut } from 'lucide-react';
import { type DivestForm, buildDivestBody, isDivestValid, useDivestInvestment } from './useDivestInvestment';

const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';
const INPUT =
  'w-full rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink';

/**
 * Modal de desinvestimento. Exige MOIC e TIR realizados + comentário (rigoroso — DEC-040). POST
 * /api/investments/{id}/divest (If-Match). Marca o investimento como Desinvestido (reversível).
 */
export function DivestModal({
  id,
  etag,
  codename,
  onClose,
}: {
  id: string;
  etag: string;
  codename: string;
  onClose: () => void;
}) {
  const divest = useDivestInvestment();
  const [form, setForm] = useState<DivestForm>({ moicRealized: '', irrRealized: '', comment: '' });

  const canConfirm = isDivestValid(form) && !divest.isPending;

  const onConfirm = () => {
    if (!canConfirm) return;
    divest.mutate({ id, etag, body: buildDivestBody(form) }, { onSuccess: onClose });
  };

  return (
    <>
      <button
        type="button"
        aria-label="Cancelar"
        onClick={onClose}
        className="fixed inset-0 z-[90] cursor-default bg-[rgba(20,18,12,0.45)]"
      />
      <div
        role="dialog"
        aria-label="Desinvestir"
        className="fixed left-1/2 top-1/2 z-[100] w-[min(440px,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-vega-surface p-5 shadow-vega-lg"
      >
        <div className="mb-1 flex items-center gap-2">
          <LogOut size={16} className="text-vega-ink-2" />
          <h2 className="text-[15px] font-semibold text-vega-ink">Desinvestir</h2>
        </div>
        <p className="mb-4 text-[12.5px] text-vega-muted-2">
          Registra a saída de <span className="text-vega-ink">{codename}</span> com as métricas
          realizadas. Reversível por "Reativar".
        </p>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className={LABEL}>MOIC real. *</span>
            <input
              value={form.moicRealized}
              onChange={(e) => setForm((f) => ({ ...f, moicRealized: e.target.value }))}
              inputMode="decimal"
              autoFocus
              placeholder="ex.: 2,5"
              className={INPUT}
            />
          </label>
          <label className="block">
            <span className={LABEL}>TIR real. (%) *</span>
            <input
              value={form.irrRealized}
              onChange={(e) => setForm((f) => ({ ...f, irrRealized: e.target.value }))}
              inputMode="decimal"
              placeholder="ex.: 18"
              className={INPUT}
            />
          </label>
        </div>

        <label className="mb-4 block">
          <span className={LABEL}>Comentário *</span>
          <textarea
            value={form.comment}
            onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
            rows={3}
            placeholder="Obrigatório — fica registrado no histórico do investimento."
            className="w-full resize-none rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={divest.isPending}
            className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-muted disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="rounded px-3 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
            style={{ background: 'var(--vega-ink)' }}
          >
            {divest.isPending ? 'Salvando…' : 'Confirmar desinvestimento'}
          </button>
        </div>
      </div>
    </>
  );
}
